import React from 'react';
import { Dropdown, Menu, Popconfirm, Tag, Tooltip } from 'antd';
import { DeleteOutlined, MoreOutlined, EyeOutlined } from '@ant-design/icons';
import moment from 'moment';
import { getUserDetails } from '../../../../utils/helper';
import type { OoReport, Report } from '../../types';
import { getStatusStyle } from './statusStyles';

interface ReportHistoryProps {
  reports: Report[];
  /** doc_id currently open in the editor — used to highlight the loaded row */
  activeDocId?: number | null;
  /** true once the order has a signed report — historical drafts open read-only then */
  orderSignedOff?: boolean;
  onLoad: (report: Report) => void;
  onDelete: (report: Report) => void;
}

const isAutosaved = (r: Report) => !r.pr_status || r.pr_status === 'AUTOSAVED';

const ReportHistory: React.FC<ReportHistoryProps> = ({
  reports, activeDocId, orderSignedOff = false, onLoad, onDelete,
}) => {
  const currentUser = getUserDetails()?.username;

  // A private autosave entry only appears once OnlyOffice has actually written content
  // (doc_version > 1) — mirrors the old app where the entry shows up on first autosave.
  const visibleReports = reports.filter(record => {
    const rec = record as OoReport;
    return !(isAutosaved(record) && (rec.document?.doc_version ?? 1) <= 1);
  });

  if (!visibleReports.length) {
    return <div className="no-content-message">No reports available</div>;
  }

  return (
    <div className="report-history-list">
      {visibleReports.map(record => {
        const rec = record as OoReport;
        const docId = rec.document?.doc_id;
        const isActive = !!docId && docId === activeDocId;
        const isSigned = record.pr_status === 'SIGNEDOFF';
        const creator = rec.pr_created_by || rec.pr_report_drafted_by || rec.pr_reported_by;
        // Signed entries are immutable records; draft/reviewed/autosaved entries can only
        // be deleted by the user who created them (backend enforces the same).
        const isDeleteLocked = isSigned || (!!creator && creator !== currentUser);
        // Once the order is signed off, historical drafts/reviews open read-only.
        // The signed report itself opens editable (re-reporting scratchpad, nothing persists).
        const opensReadOnly = !isSigned && orderSignedOff;

        const menu = (
          <Menu>
            <Menu.Item key="load" icon={<EyeOutlined />} onClick={() => onLoad(record)}>
              {opensReadOnly ? 'View' : 'Open'}
            </Menu.Item>
            <Menu.Item key="delete" icon={<DeleteOutlined />} disabled={isDeleteLocked}>
              <Popconfirm
                title="Delete report?"
                description="This will permanently delete the report and its document. This action cannot be undone."
                onConfirm={() => onDelete(record)}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                disabled={isDeleteLocked}
              >
                Delete
              </Popconfirm>
            </Menu.Item>
          </Menu>
        );

        return (
          <div
            key={record.pr_id}
            className={`report-history-row${isActive ? ' active' : ''}`}
            onClick={() => onLoad(record)}
            title={
              isSigned ? 'Open the signed report (edits are only kept if you sign off again)'
                : opensReadOnly ? 'View this historical version (read-only)'
                : 'Open this report'
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              marginBottom: 4,
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12,
              borderLeft: `3px solid ${isActive ? '#1677ff' : 'transparent'}`,
              background: isActive ? '#E6F4FF' : 'transparent',
            }}
          >
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Line 1: status + user */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <Tag
                  style={{ ...getStatusStyle(record.pr_status || 'AUTOSAVED'), margin: 0, fontSize: 11, lineHeight: '18px' }}
                >
                  {isAutosaved(record) ? 'AUTO-SAVED' : record.pr_status?.replaceAll('_', ' ')}
                </Tag>
                <span
                  style={{
                    color: '#334155',
                    fontWeight: 500,
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {record.pr_reported_by || '—'}
                </span>
              </div>
              {/* Line 2: datetime + loaded indicator */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b' }}>
                <span style={{ whiteSpace: 'nowrap' }}>
                  {moment(record.createdAt).format('DD-MM-YY hh:mm A')}
                </span>
                {isActive && (
                  <span style={{ color: '#1677ff', fontWeight: 600, whiteSpace: 'nowrap' }}>● loaded</span>
                )}
              </div>
            </div>
            <Tooltip title="Actions">
              <Dropdown overlay={menu} trigger={['click']}>
                <MoreOutlined
                  onClick={e => e.stopPropagation()}
                  style={{ fontSize: '1rem', cursor: 'pointer' }}
                />
              </Dropdown>
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
};

export default ReportHistory;
